import React, { useContext, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useBlocker } from "react-router-dom";
import styles from "./Settings.module.scss";
import { ThemeContext } from "../../context/ThemeContext";
import { useUser } from "../../context/UserContext";
import { useConfig } from "../../context/ConfigContext";
import { useAlert } from "../../hooks/useAlert";
import { LinkButton } from "../../components/LinkButton/LinkButton";
import { SkeletonContainer } from "../../components/Skeleton";
import { SettingsSkeleton } from "./components/SettingsSkeleton";
import { api } from "../../services/api";

type SettingsForm = {
  theme: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  alert: number;
  privacy: {
    profileVisible: boolean;
    showOnlineStatus: boolean;
  };
  language: string;
};

const LOCAL_STORAGE_KEY = "userSettingsDraft";

const Settings: React.FC = () => {
  const { setTheme, availableThemes } = useContext(ThemeContext);
  const { logout, user } = useUser();
  const { config, loading, updateConfig } = useConfig();
  const { alert, confirm } = useAlert();
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);

  const NotificationOptions = [
    { label: "All", value: 2 },
    { label: "Mentions Only", value: 1 },
    { label: "None", value: 0 },
  ];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<SettingsForm>({
    defaultValues: {
      theme: config?.theme ?? "theme-steam",
      notifications: {
        email: config?.notifications?.email ?? true,
        push: config?.notifications?.push ?? true,
        sms: config?.notifications?.sms ?? false,
      },
      alert: config?.alert ?? 2,
      privacy: {
        profileVisible: config?.privacy?.profileVisible ?? true,
        showOnlineStatus: config?.privacy?.showOnlineStatus ?? true,
      },
      language: config?.language ?? "en",
    },
  });

  // Load stored draft
  useEffect(() => {
    const stored = sessionStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored) {
      try {
        reset(JSON.parse(stored));
      } catch {
        console.warn("Failed to parse stored settings draft");
      }
    }
  }, [reset]);

  // Keep synced with external config
  useEffect(() => {
    if (!config) return;
    reset({
      theme: config.theme ?? "theme-steam",
      notifications: {
        email: config.notifications?.email ?? true,
        push: config.notifications?.push ?? true,
        sms: config.notifications?.sms ?? false,
      },
      alert: config.alert ?? 2,
      privacy: {
        profileVisible: config.privacy?.profileVisible ?? true,
        showOnlineStatus: config.privacy?.showOnlineStatus ?? true,
      },
      language: config.language ?? "en",
    });
    sessionStorage.removeItem(LOCAL_STORAGE_KEY);
  }, [config, reset]);

  const values = watch();
  useEffect(() => {
    sessionStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(values));
  }, [values]);

  const watchedTheme = watch("theme");
  useEffect(() => {
    if (setTheme && watchedTheme) setTheme(watchedTheme);
  }, [watchedTheme, setTheme]);

  // Warn before leaving page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const onSubmit = (data: SettingsForm) => {
    updateConfig({ ...data, isPrivate: !data.privacy.profileVisible });
    sessionStorage.removeItem(LOCAL_STORAGE_KEY);
  };

  const blocker = useBlocker(isDirty);
  useEffect(() => {
    if (blocker.state === "blocked") {
      (async () => {
        const shouldSave = await confirm({
          message: "You're about to leave without saving. Would you like to save before you navigate?",
          type: "warning",
          confirmText: "Save & Leave",
          cancelText: "Leave Without Saving",
        });
        if (shouldSave) {
          handleSubmit(onSubmit)();
        }
        blocker.proceed();
      })();
    }
  }, [blocker, confirm]);

  return (
    <div className={styles.settingsPage}>
      <SkeletonContainer
        loading={loading}
        skeleton={<SettingsSkeleton />}
        label="settings"
      >
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Sign Out</h2>
          <div className={styles.settingItem}>
            <label>Sign out of your account</label>
            <button type="button" onClick={logout} className={styles.actionBtn}>
              Log out
            </button>
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Link Accounts</h2>

          <div className={styles.linkGrid}>
            <div className={styles.linkCell}><LinkButton platform="steam" /></div>
            <div className={styles.linkCell}><LinkButton platform="discord" /></div>
            <div className={styles.linkCell}><LinkButton platform="epicgames" /></div>
          </div>
        </section>

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Sign Out */}


          {/* Account */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Account</h2>
            <div className={styles.settingItem}>
              <label>Username</label>
              <input type="text" value={user?.username || ""} readOnly />
            </div>
            <div className={styles.settingItem}>
              <label>Email</label>
              <input type="email" value={user?.email || ""} readOnly />
            </div>
          </section>

          {/* Preferences */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Preferences</h2>
            <div className={styles.settingItem}>
              <label>Notification Alerts</label>
              <Controller
                name="alert"
                control={control}
                render={({ field }) => (
                  <select
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                  >
                    {NotificationOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className={styles.settingItem}>
              <label>Email Notifications</label>
              <Controller
                name="notifications.email"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>

            <div className={styles.settingItem}>
              <label>Push Notifications</label>
              <Controller
                name="notifications.push"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>

            <div className={styles.settingItem}>
              <label>SMS Notifications</label>
              <Controller
                name="notifications.sms"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>

            <div className={styles.settingItem}>
              <label>Theme</label>
              <Controller
                name="theme"
                control={control}
                render={({ field }) => (
                  <select {...field}>
                    {availableThemes.map((t) => (
                      <option key={t.className} value={t.className}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                )}
              />
            </div>

            <div className={styles.settingItem}>
              <label>Language</label>
              <Controller
                name="language"
                control={control}
                render={({ field }) => (
                  <select {...field}>
                    <option value="en">English</option>
                    <option value="es">Español</option>
                    <option value="de">Deutsch</option>
                    <option value="fr">Français</option>
                  </select>
                )}
              />
            </div>
          </section>

          {/* Privacy */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Privacy</h2>
            <div className={styles.settingItem}>
              <label>Profile Visible</label>
              <Controller
                name="privacy.profileVisible"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>

            <div className={styles.settingItem}>
              <label>Show Online Status</label>
              <Controller
                name="privacy.showOnlineStatus"
                control={control}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
          </section>

          {/* Security */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Security</h2>
            <div className={styles.settingItem}>
              <label>Password</label>
              {passwordResetSent ? (
                <span className={styles.successText}>Reset link sent to your email</span>
              ) : (
                <button
                  type="button"
                  className={styles.actionBtn}
                  disabled={passwordResetLoading}
                  onClick={async () => {
                    if (!user?.email) return;
                    setPasswordResetLoading(true);
                    try {
                      await api.app.forgotPassword(user.email);
                      setPasswordResetSent(true);
                    } catch (err) {
                      alert({ message: "Failed to send reset email. Please try again.", type: "error" });
                    } finally {
                      setPasswordResetLoading(false);
                    }
                  }}
                >
                  {passwordResetLoading ? "Sending..." : "Change Password"}
                </button>
              )}
            </div>
            <div className={styles.settingItem}>
              <label>Two-Factor Auth</label>
              <button type="button" className={styles.actionBtn}>
                Enable
              </button>
            </div>
          </section>

          {/* Save */}
          <section className={styles.section}>
            <div className={styles.settingItem}>
              <label>Save</label>
              <button type="submit" className={styles.actionBtn} disabled={!isDirty}>
                Save Changes
              </button>
            </div>
          </section>

          {/* Legal */}
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Legal</h2>
            <div className={styles.settingItem}>
              <label>Privacy Policy</label>
              <a href="/privacy" className={styles.actionBtn} target="_blank" rel="noopener noreferrer">
                View
              </a>
            </div>
            <div className={styles.settingItem}>
              <label>Terms of Service</label>
              <a href="/terms" className={styles.actionBtn} target="_blank" rel="noopener noreferrer">
                View
              </a>
            </div>
          </section>
        </form>
      </SkeletonContainer>
    </div>
  );
};

export default Settings;
